import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Panel, PanelHeader} from '@Components/panel';
import {DownloadPanelFooter} from '@Components/download-panel/components/download-panel-footer/download-panel-footer';
import styles from '@Components/download-panel/download-panel.module.scss';
import {FileTypeOption} from '@Components/download-panel/components/file-type-option/file-type-option';
import {DownloadDesignSizeOption} from '@Components/download-panel/components/download-design-size-option/download-design-size-option';
import {DownloadMoreOptions} from '@Components/download-panel/components/download-more-options/download-more-options';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import type {DownloadCallbackResponse, DownloadFreebieResponse} from '@Libraries/poster-download-library';
import {shouldShowDownloadUpsellingForUser} from '@Libraries/poster-download-library';
import {
  CHEAP_PRODUCT_ID,
  DownloadDialogSource,
  type DownloadPosterAPIData,
  DownloadPublishMode,
  EXPENSIVE_PRODUCT_ID,
  getFreebieStatus,
  getOrderProgress,
  isDurationEnabledForGif,
  onDownloadFreebie,
  onDownloadPoster,
  onDownloadPosterUnpurchased,
  triggerBrowserDownload,
  VideoDesignSizeOptionType,
} from '@Libraries/poster-download-library';
import {FileTypeOptions} from '@Components/download-panel/components/file-type-option/file-type-option.types';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {
  DownloadStateTypes,
  setUpsellingRecommendationNumberIfNeeded,
  updateCurrentProcessingOrderId,
  updateDownloadLoadingPopupOpenState,
  updateOrderState,
  updateSliderDragState,
} from '@Components/download-panel/download-panel.reducer';
import {DownloadPanelDownloadingSection} from '@Components/download-panel/components/download-panel-downloading-section/download-panel-downloading-section';
import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {
  AJAXResponseOrderSuccess,
  AJAXResponsePurchaseProduct,
} from '@Panels/poster-publishing-options-panel/components/publishing-embed-in-webpage-panel/publishing-embed-in-webpage-panel.types';
import {isPremiumUser} from '@Utils/user.util';
import type {PosterLoadObject} from '@PosterWhiteboard/poster/poster.types';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {DownloadPanelUpselling} from '@Components/download-panel/components/download-panel-upselling/download-panel-upselling';
import {openGoogleDriveFolderPicker, saveToGoogleDriveWithSelectedFolder} from '@Libraries/google-drive-download-picker';
import {getStateFromUrl} from '@Utils/url.util';
import {isIOSAppOrEmbeddedEditor} from '@Utils/browser.util';
import {showEditorNotificationMessageGrowl} from '@Components/poster-editor/library/poster-editor-library';
import {makeFilenameSafe} from '@Utils/file.util';
import {saveFileToDropbox} from '@Libraries/dropbox-saver';
import {AnimatePresence, motion} from 'framer-motion';
import {LetterSizeOptionType} from '@Components/download-panel/components/download-adjust-letter-size-option/download-adjust-letter-size-option.types';
import {ENABLED_DOWNLOAD_UPSELLING_DIALOG_NUMBER} from '@Components/download-panel/components/download-panel-upselling/download-panel-upselling.types';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {onConvertTrialFailCallback} from '@Panels/poster-publishing-options-panel/publishing-process-design-library';
import {DownloadPdfQualityOption} from '@Components/download-panel/components/download-pdf-quality-option/download-pdf-quality-option';
import {DOWNLOAD_GIF_DEFAULT_DURATION} from '@Components/download-panel/components/download-gif-duration-option/download-gif-duration-option.types';
import {DownloadGettyRequirementInfoBox} from '@Components/download-panel/components/download-getty-requirement-info-box/download-getty-requirement-info-box';
import {updateModalDisableOutsideClickDismiss} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import {DownloadPanelInfoBox} from '@Components/download-panel/components/download-panel-info-box/download-panel-info-box';

interface DownloadPanelContentManagerProps {
  id: string;
  className?: string;
  isOpenedInModal?: boolean;
  isOpenedInEditor?: boolean;
  isOpenedInPrintShop?: boolean;
}

interface DownloadPollingFunctionResponse {
  downloadUrl: string;
  printShopLink: string;
  idOrder: string;
  isError?: boolean;
}

interface FreebiePollFinishResponse {
  isAvailable: boolean;
  data: string;
}

export function DownloadPanelContentManager({
  className = '',
  isOpenedInPrintShop = false,
  isOpenedInEditor = true,
  isOpenedInModal = false,
  ...props
}: DownloadPanelContentManagerProps): ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [bottomScrollInitializer, setBottomScrollInitializer] = useState<boolean>(false);
  const {bottomShadow} = useTopBottomScrollShadow(containerRef, bottomScrollInitializer);
  const {posterObject} = useDownloadPanelData();
  const {
    scale,
    transparency,
    posterVersionId,
    meetsGettyRequirements,
    productsData,
    margin,
    bleed,
    duration,
    videoScale,
    bleedUnit,
    isPDFWebQuality,
    colorProfile,
    letterSize,
    speed,
    fileType,
  } = useDownloadPanelData();
  const dispatch = useAppDispatch();
  const currentDownloadOrderId = useAppSelector((state) => {
    return state.downloadDialog.currentProcessingOrderId;
  });
  const orderHashmap = useAppSelector((state) => {
    return state.downloadDialog.orderStateHashmap;
  });
  const [hasClosedUpsellingInThisSession, setHasClosedUpsellingInThisSession] = useState(false);
  const isDownloadInErrorState = currentDownloadOrderId && orderHashmap[currentDownloadOrderId].state === DownloadStateTypes.ERROR;
  const isDownloadInProgressOrReady =
    currentDownloadOrderId &&
    (orderHashmap[currentDownloadOrderId].state === DownloadStateTypes.DOWNLOADING || orderHashmap[currentDownloadOrderId].state === DownloadStateTypes.READY);
  const [isInitialDownloadInProgress, setIsInitialDownloadInProgress] = useState(false);
  const downloadProgress = useRef(0);
  const [, setDownloadProgressState] = useState(0);
  const onClose = useDefaultPanelClose(props.id, isOpenedInModal);
  const oauthUrl = useRef('');
  const state = useRef('');
  const [isDropboxPendingSave, setIsDropboxPendingSave] = useState<boolean>(false);

  useEffect((): void => {
    setBottomScrollInitializer(!bottomScrollInitializer);
  }, [fileType]);

  useEffect(() => {
    dispatch(setUpsellingRecommendationNumberIfNeeded(Math.floor(Math.random() * ENABLED_DOWNLOAD_UPSELLING_DIALOG_NUMBER)));
  }, []);

  const toggleModalOverlayClick = (toggle: boolean): void => {
    if (isOpenedInModal) {
      dispatch(
        updateModalDisableOutsideClickDismiss({
          modalId: MODALS_IDS.DOWNLOAD_MODAL,
          disableOutsideClickDismiss: toggle,
        })
      );
      return;
    }

    if (isOpenedInEditor) {
      dispatch(updateSliderDragState(toggle));
    }
  };

  const onSlide = (): void => {
    toggleModalOverlayClick(true);
  };
  const onSlideEnded = (): void => {
    setTimeout((): void => {
      toggleModalOverlayClick(false);
    }, 300);
  };

  const onDownloadUpdatedDesign = (): void => {
    onClickDownload(true);
  };

  const getOptionsForType = (): ReactElement => {
    return (
      <>
        <div className={`spacing-p-t-3 spacing-p-b-3 spacing-p-r-4 spacing-p-l-4 flexbox flex-v-row gap-4`}>
          <DownloadPanelInfoBox onDownloadUpdatedDesignClick={onDownloadUpdatedDesign} />
          <DownloadGettyRequirementInfoBox />
          <FileTypeOption />
          <DownloadPdfQualityOption />
          {/*{isPremiumUser() ||  ? null : <DownloadQualityOption />}*/}
          <DownloadDesignSizeOption onSlideCallback={onSlide} onSlideEndedCallback={onSlideEnded} />
        </div>
        <DownloadMoreOptions
          onSlideCallback={onSlide}
          onSlideEndedCallback={onSlideEnded}
          onToggleOpenClose={(): void => {
            setTimeout((): void => {
              setBottomScrollInitializer(!bottomScrollInitializer);
            }, 300);
          }}
        />
      </>
    );
  };

  const onClickBack = (): void => {
    dispatch(updateCurrentProcessingOrderId(undefined));
  };

  const doShowUpselling = (): boolean => {
    if (!shouldShowDownloadUpsellingForUser()) {
      return false;
    }
    if (isOpenedInModal || !isOpenedInEditor) {
      return false;
    }
    if (isDownloadInProgressOrReady) {
      if (currentDownloadOrderId && orderHashmap[currentDownloadOrderId]) {
        return !hasClosedUpsellingInThisSession;
      }
    }

    return false;
  };

  const shouldShowUpselling = doShowUpselling();
  const [isUpsellingVisible, setIsUpsellingVisible] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (shouldShowUpselling) {
      timer = setTimeout(() => {
        setIsUpsellingVisible(true);
      }, 200);
    } else {
      setIsUpsellingVisible(false);
    }

    return (): void => {
      clearTimeout(timer);
    };
  }, [shouldShowUpselling]);

  const getPanelContentWrapper = (): ReactElement<any> => {
    return (
      <div ref={containerRef} className="_position-relative _full-height _overflow-auto">
        {getPanelContent()}
        <AnimatePresence>
          {isUpsellingVisible ? (
            <motion.div
              initial={{opacity: 0}}
              animate={{opacity: 1}}
              exit={{opacity: 0}}
              transition={{duration: 0}}
              style={{
                top: wrapperRef.current ? wrapperRef.current.getBoundingClientRect().bottom + 12 : undefined,
              }}
              className={styles.webUpselling}
            >
              <DownloadPanelUpselling
                onClose={(): void => {
                  setHasClosedUpsellingInThisSession(true);
                  setTimeout((): void => {
                    dispatch(setUpsellingRecommendationNumberIfNeeded(null));
                  }, 300);
                }}
              />
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    );
  };

  const getPanelContent = (): ReactElement => {
    if (isDownloadInProgressOrReady || isDownloadInErrorState) {
      if (currentDownloadOrderId || orderHashmap[currentDownloadOrderId]) {
        return (
          <DownloadPanelDownloadingSection
            name={orderHashmap[currentDownloadOrderId]?.name ?? posterObject.name}
            fileType={fileType}
            hasClosedUpsellingInThisSession={hasClosedUpsellingInThisSession}
            onCloseUpselling={(): void => {
              setHasClosedUpsellingInThisSession(true);
            }}
            isOpenedInPrintShop={isOpenedInPrintShop}
            onSaveToDropbox={saveCurrentOrderToDropbox}
            showDropboxSaveButton={isDropboxPendingSave}
            isOpenedInEditor={isOpenedInEditor}
            onClickBack={onClickBack}
            isOpenedInModal={isOpenedInModal}
          />
        );
      }
    }

    return <div className={`_full-width _full-height ${styles.container}`}>{getOptionsForType()}</div>;
  };

  const getDataAccordingToType = (): DownloadPosterAPIData => {
    switch (fileType) {
      case FileTypeOptions.GIF:
        return {
          type: FileTypeOptions.GIF,
          downloadOptions: {
            speed: isDurationEnabledForGif(posterObject) ? speed : 1,
            scale: scale,
            duration: isDurationEnabledForGif(posterObject) ? duration : DOWNLOAD_GIF_DEFAULT_DURATION,
          },
        };
      case FileTypeOptions.JPG:
        return {
          type: FileTypeOptions.JPG,
          downloadOptions: {
            bleedUnits: bleedUnit,
            bleedValue: bleed,
            scale: scale,
          },
        };
      case FileTypeOptions.PNG:
        return {
          type: FileTypeOptions.PNG,
          downloadOptions: {
            bleedUnits: bleedUnit,
            isTransparent: transparency,
            bleedValue: bleed,
            scale: scale,
          },
        };
      case FileTypeOptions.PDF:
        return {
          type: fileType,
          downloadOptions: {
            bleedUnits: bleedUnit,
            bleedValue: bleed,
            hasMargin: margin,
            colorProfile: colorProfile,
            letterSize: letterSize === LetterSizeOptionType.NO ? undefined : letterSize,
            isWebQuality: isPDFWebQuality,
          },
        };
      case FileTypeOptions.MP4:
        return {
          type: FileTypeOptions.MP4,
          downloadOptions: {
            speed: speed,
            scale: scale,
            duration: duration,
          },
        };
      case FileTypeOptions.WEBM:
        console.log('todo');
        break;
      case FileTypeOptions.WEBP:
        console.log('todo');
        break;
      default:
        throw new Error('File type not supported yet');
    }

    throw new Error('File type not supported yet');
  };

  const triggerDownloadScreen = (id: string): void => {
    dispatch(updateCurrentProcessingOrderId(id));
    dispatch(
      updateOrderState({
        orderId: id,
        state: DownloadStateTypes.DOWNLOADING,
        name: posterObject.name,
        fileType: fileType,
      })
    );
  };

  const startDownloadForPremiumUser = async (ignorePosterVersionIdIfPresent = false): Promise<void> => {
    const finalDownloadedData = await initPremiumDownload(posterObject, ignorePosterVersionIdIfPresent);
    if (finalDownloadedData.isError) {
      dispatch(
        updateOrderState({
          state: DownloadStateTypes.ERROR,
          orderId: finalDownloadedData.idOrder,
          downloadProgress: 100,
          name: posterObject.name,
          fileType: fileType,
        })
      );
      return;
    }
    processFinalDownloadedData(finalDownloadedData);
  };

  const startDownload = async (ignorePosterVersionIdIfPresent = false): Promise<void> => {
    if (!meetsGettyRequirements) {
      return;
    }
    try {
      setIsInitialDownloadInProgress(true);
      if (isPremiumUser() || isOpenedInPrintShop) {
        if (productsData && productsData?.length > 0 && productsData[0].status === 'out-of-free-downloads') {
          window.PMW.premiumDialogs.openConvertTrialDialog(
            productsData[0].status,
            () => {
              void startDownloadForPremiumUser();
              return;
            },
            (): void => {
              onConvertTrialFailCallback(productsData[0].status);
              return;
            },
            (): void => {
              return;
            }
          );
          return;
        }
        await startDownloadForPremiumUser(ignorePosterVersionIdIfPresent);
        return;
      } else {
        console.error('to be done');
      }

      // TODO: Add conditions to either call initPremiumDownload or initCheckoutDownload
      // if (premium download) {
      // const finalDownloadedData = await initPremiumDownload(poster);
      // console.log('finalDownloadedData', finalDownloadedData);
      // processFinalDownloadedData(finalDownloadedData);

      // } else if (payg download) {
      // await initCheckoutDownload(poster);

      // } else if (freebie download) {
      // const downloadUrl = await initFreebieDownload(poster);
      // console.log('downloadUrl', downloadUrl);
      // TODO: do download
      // }
    } catch (error) {
      console.error('Download error:', error);
      dispatch(updateCurrentProcessingOrderId('default-error-order-id'));
      dispatch(
        updateOrderState({
          state: DownloadStateTypes.ERROR,
          orderId: 'default-error-order-id',
          downloadProgress: 100,
          name: posterObject.name,
          fileType: fileType,
        })
      );
    } finally {
      setIsInitialDownloadInProgress(false);
    }
  };

  const onDownloadToDrive = async (): Promise<void> => {
    if (isPremiumUser() || isOpenedInPrintShop) {
      const oauthUrl = await getOauthUrl();
      await openGoogleDriveFolderPicker(oauthUrl, onGoogleDriveFolderSelected);
    } else {
      console.error('to be done');
      return;
    }
  };

  const onGoogleDriveFolderSelected = async (): Promise<void> => {
    const finalDownloadedData = await initPremiumDownload(posterObject);
    dispatch(
      updateOrderState({
        state: DownloadStateTypes.READY,
        orderId: finalDownloadedData.idOrder,
        downloadProgress: 100,
        downloadURL: finalDownloadedData.downloadUrl,
        name: posterObject.name,
        fileType: fileType,
      })
    );
    const downloadUrl = finalDownloadedData.downloadUrl;
    const downloadFilename = makeFilenameSafe(posterObject.name, 'poster');
    await saveToGoogleDriveWithSelectedFolder(downloadUrl, downloadFilename);
    showEditorNotificationMessageGrowl(window.i18next.t('pmwjs_saved'));
  };

  const onDownloadToDropbox = async (): Promise<void> => {
    if (!isPremiumUser() && !isOpenedInPrintShop) return;
    setIsDropboxPendingSave(true);
    const finalDownloadedData = await initPremiumDownload(posterObject);
    if (!currentDownloadOrderId) {
      dispatch(updateCurrentProcessingOrderId(finalDownloadedData.idOrder));
    }
    dispatch(
      updateOrderState({
        state: DownloadStateTypes.READY,
        orderId: finalDownloadedData.idOrder,
        downloadProgress: 100,
        downloadURL: finalDownloadedData.downloadUrl,
        printShopLink: finalDownloadedData.printShopLink,
      })
    );
  };

  const getOauthUrl = async (): Promise<string> => {
    if (oauthUrl.current) {
      return oauthUrl.current;
    }
    oauthUrl.current = (await window.PMW.readLocal('authenticate/getCurrentUserGoogleUploadToken', {
      isIOSAppOrEmbeddedEditor: isIOSAppOrEmbeddedEditor(),
    })) as string;
    if (!oauthUrl.current) {
      // TODO: show error
      throw new Error('Invalid oauthUrl');
    }
    state.current = getStateFromUrl(oauthUrl.current);
    return oauthUrl.current;
  };

  const processFinalDownloadedData = (finalDownloadedData: DownloadPollingFunctionResponse): void => {
    const downloadSafePosterName = makeFilenameSafe(posterObject.name, 'poster');
    triggerBrowserDownload(finalDownloadedData.downloadUrl, downloadSafePosterName, fileType);
    if (!currentDownloadOrderId) {
      dispatch(updateCurrentProcessingOrderId(finalDownloadedData.idOrder));
    }
    dispatch(
      updateOrderState({
        state: DownloadStateTypes.READY,
        orderId: finalDownloadedData.idOrder,
        downloadProgress: 100,
        downloadURL: finalDownloadedData.downloadUrl,
        printShopLink: finalDownloadedData.printShopLink,
        name: posterObject.name,
        fileType: fileType,
      })
    );
  };

  const getSource = (): DownloadDialogSource => {
    if (isOpenedInPrintShop) {
      return DownloadDialogSource.DOWNLOAD_OPTIONS_PAGE;
    }

    if (isOpenedInEditor) {
      return DownloadDialogSource.EDITOR;
    } else {
      return DownloadDialogSource.MYSTUFF;
    }
  };

  const triggerGA4event = (): void => {
    trackPosterBuilderGA4Events(GA4EventName.DOWNLOAD_OPTIONS, {[GA4EventParamName.SOURCE]: getSource()});
  };

  const onClickDownload = (ignorePosterVersionIdIfPresent = false): void => {
    triggerGA4event();
    dispatch(updateDownloadLoadingPopupOpenState(true));
    void startDownload(ignorePosterVersionIdIfPresent);
  };

  const getProductIdFromOptions = (): number => {
    const data = getDataAccordingToType();
    switch (data.type) {
      case FileTypeOptions.JPG:
      case FileTypeOptions.PNG:
        return scale > 1 ? EXPENSIVE_PRODUCT_ID : CHEAP_PRODUCT_ID;
      case FileTypeOptions.GIF:
        if (duration > 10 || videoScale === VideoDesignSizeOptionType.FULL_HD || videoScale === VideoDesignSizeOptionType.UHD_4K) {
          return EXPENSIVE_PRODUCT_ID;
        }
        return CHEAP_PRODUCT_ID;
      case FileTypeOptions.MP4:
        if (duration > 20 || videoScale === VideoDesignSizeOptionType.FULL_HD || videoScale === VideoDesignSizeOptionType.UHD_4K) {
          return EXPENSIVE_PRODUCT_ID;
        }
        return CHEAP_PRODUCT_ID;
      case FileTypeOptions.PDF:
        return EXPENSIVE_PRODUCT_ID;
      default:
        return CHEAP_PRODUCT_ID;
    }
  };

  const initPremiumDownload = async (poster: PosterLoadObject, ignorePosterVersionIdIfPresent = false): Promise<DownloadPollingFunctionResponse> => {
    return (await window.PMW.pollingAjaxCallAsync(async () => {
      const data = await onDownloadPoster(
        {
          productId: getProductIdFromOptions(),
          hashedId: poster.hashedID,
          isSilent: false,
          posterVersionId: ignorePosterVersionIdIfPresent ? undefined : (posterVersionId ?? undefined),
          isFromDownloadOptionsPage: isOpenedInPrintShop,
        },
        getDataAccordingToType()
      );
      setIsInitialDownloadInProgress(false);
      return data;
    }, processDownloadCallbackResponse)) as Promise<DownloadPollingFunctionResponse>;
  };

  const initFreebieDownload = async (poster: Poster): Promise<string> => {
    return (await window.PMW.pollingAjaxCallAsync(async () => {
      const data = await onDownloadFreebie(
        {
          productId: 86,
          hashedId: poster.hashedID,
          isSilent: poster.isVideo(),
        },
        getDataAccordingToType()
      );
      setIsInitialDownloadInProgress(false);
      // triggerDownloadScreen(data.idOrder);
      return data;
    }, processFreebieDownloadCallbackResponse)) as Promise<string>;
  };

  const initCheckoutDownload = async (poster: Poster): Promise<void> => {
    const data = await onDownloadPosterUnpurchased(
      {
        hashedId: poster.hashedID,
        productId: 86,
        promotion: '',
        mode: DownloadPublishMode.DOWNLOAD,
        // TODO: Sending USD for now, need to get actual currency code
        // currencyCode: productInfo.currencyCode,
        currencyCode: 'USD',
        isInAppPurchase: window.PMW.isIOSApp(),
      },
      getDataAccordingToType()
    );
    console.log('data from onDownloadPosterUnpurchased', data);
    processCheckoutResponse(data);
  };

  const processDownloadCallbackResponse = async (downloadResponse: DownloadCallbackResponse) => {
    if ('downloadUrl' in downloadResponse && downloadResponse.downloadUrl !== '') {
      return {
        isAvailable: true,
        data: {
          downloadUrl: downloadResponse.downloadUrl,
          idOrder: downloadResponse.idOrder,
          printShopLink: downloadResponse.printShopLink,
        },
      };
    }
    triggerDownloadScreen(downloadResponse.idOrder);
    const orderProgress = await getOrderProgress({
      id: downloadResponse.idOrder,
      percentageDone: downloadProgress.current,
      sleepTime: 3,
    });

    if ('error' in orderProgress) {
      return {
        isAvailable: true,
        data: {
          idOrder: downloadResponse.idOrder,
          isError: true,
        },
      };
    }

    setDownloadProgressState(orderProgress.percentageDone);

    if (orderProgress.percentageDone === 100) {
      return {
        isAvailable: true,
        data: {
          downloadUrl: orderProgress.downloadUrl,
          idOrder: downloadResponse.idOrder,
          printShopLink: orderProgress.printShopLink,
        },
      };
    } else {
      downloadProgress.current = orderProgress.percentageDone;
      dispatch(
        updateOrderState({
          orderId: downloadResponse.idOrder,
          downloadProgress: orderProgress.percentageDone,
        })
      );
      return {
        isAvailable: false,
        idOrder: downloadResponse.idOrder,
      };
    }
  };

  const processFreebieDownloadCallbackResponse = async (data: DownloadFreebieResponse): Promise<FreebiePollFinishResponse> => {
    if (data.downloadUrl) {
      return {
        isAvailable: true,
        data: data.downloadUrl,
      };
    }
    if (data.generationKey) {
      const freebiesStatusData = await getFreebieStatus(data.generationKey);
      return {
        isAvailable: true,
        data: freebiesStatusData.data.downloadUrl,
      };
    }
    // TODO: throw error
    throw new Error('Neither downloadUrl nor generationKey available');
  };

  // This function is equivalent to processDownloadCallbackResponse for payg flo
  const onCheckoutSuccess = (data: AJAXResponseOrderSuccess): void => {
    console.log('data', data);
  };

  const processCheckoutResponse = (response: AJAXResponsePurchaseProduct): void => {
    // debugger;
    if (response === null) {
      // TODO: Show general error
      // showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
    } else {
      if (response.orderRegen) {
        window.PMW.freeEditDialog(response);
        return;
      }
      if (response.message && response.premium > 0) {
        // TODO: Show error
        // showErrorOnPanel(window.i18next.t('pmwjs_cannot_purchase_premium_content'));
      } else if (response.url) {
        window.location.href = response.url;
      } else if (response.isPremiumUser || response.checkout) {
        callBackForCheckoutThroughReactBuilder(response);
      } else {
        // TODO: Show general error
        // showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
      }
    }
  };

  function callBackForCheckoutThroughReactBuilder(response: AJAXResponsePurchaseProduct): void {
    if (response.isPremiumUser) {
      onStripeCheckoutPreProcessing(response);
    } else if (response.checkout) {
      onCheckoutForV2(response);
    } else {
      // TODO: show error
      // showErrorOnPanel(window.i18next.t('pmwjs_checkout_download_error'));
    }
  }

  const onCheckoutForV2 = (response: AJAXResponsePurchaseProduct): void => {
    // if (typeof response.checkout.doUpsellPremium !== 'undefined') {
    //   // Set the flag to show trial dialog on download purchase.
    //   showTrialDialog = response.checkout.doUpsellPremium;
    // }
    onStripeCheckoutForDownloadPurchase(response);
  };

  const onStripeCheckoutForDownloadPurchase = (response: AJAXResponsePurchaseProduct): void => {
    window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', 'stripeCheckoutDialog', true, () => {
      window.PMW.stripeCheckout.showStripeCheckoutForDownloadPurchase(
        response?.checkout?.priceId ?? 0,
        parseInt(response?.checkout?.id ?? '', 10),
        response?.checkout?.posterHash ?? '',
        parseInt(response?.checkout?.idProduct, 10),
        response.checkout.is,
        onCheckoutSuccess,
        () => {
          return;
        }
      );
    });
  };

  const onStripeCheckoutPreProcessing = (response: AJAXResponsePurchaseProduct): void => {
    // let status: Status = Status.UNDEFINED;
    // let idProduct = 0;
    // if (response.status && response.status.length > 0) {
    //   status = response.status;
    // }
    //
    // if (typeof response.premiumPurchase !== 'undefined') {
    //   idProduct = response.premiumPurchase.idProduct;
    //   if (idProduct > 0) {
    //     if (status === Status.NEW_ORDER) {
    //       executeOnPosterSave(() => {
    //         newPremiumDownload(idProduct, productUsed.posterId);
    //       });
    //     } else if (status === Status.OUT_OF_FREE_DOWNLOADS) {
    //       window.PMW.gtm.trackGA4CustomEvent('TrialUserOutOfDownloads');
    //       openCheckoutDialogForTrialConversion(status, idProduct);
    //     } else {
    //       showErrorOnPanel(window.i18next.t('pmwjs_checkout_download_error'));
    //     }
    //   } else {
    //     showErrorOnPanel(window.i18next.t('pmwjs_checkout_download_error'));
    //   }
    // } else {
    //   showErrorOnPanel(window.i18next.t('pmwjs_checkout_download_error'));
    // }
  };

  function executeOnPosterSave(callback: VoidFunction): void {
    void window.posterEditor?.whiteboard?.savePoster?.save().then(callback);
  }

  function openCheckoutDialogForTrialConversion(trialConversionMode: string, productId: number): void {
    // if (window.PMW.enableCheckoutV2) {
    //   window.PMW.premiumDialogs.openConvertTrialDialog(
    //     trialConversionMode,
    //     onConvertTrial.bind(null, productId, undefined),
    //     onConvertTrialFailCallback.bind(trialConversionMode),
    //     onCheckoutCloseCallback
    //   );
    // } else {
    //   window.PMW.checkout.showCheckoutDialog(window.PMW.checkout.MODE_CONVERT_TRIAL, {
    //     cb: (data: AJAXResponseOutOfFreeDownloads): void => {
    //       onConvertTrial(productId, data);
    //     },
    //     onCheckoutFailCallback: onConvertTrialFailCallback.bind(null, trialConversionMode),
    //     trialConversionMode,
    //   });
    // }
  }

  const getPanelHeader = (): ReactElement<any> | undefined => {
    if (!isOpenedInModal) {
      return undefined;
    }

    if (isOpenedInPrintShop) {
      return undefined;
    }

    let text = window.i18next.t('pmwjs_download');
    let showBackButton = false;

    if ((isDownloadInProgressOrReady || isDownloadInErrorState) && currentDownloadOrderId) {
      if (orderHashmap[currentDownloadOrderId].state === DownloadStateTypes.DOWNLOADING) {
        text = window.i18next.t('pmwjs_downloading');
      } else if (orderHashmap[currentDownloadOrderId].state === DownloadStateTypes.READY) {
        showBackButton = true;
        text = window.i18next.t('pmwjs_enjoy_the_download');
      } else {
        showBackButton = true;
      }
    }

    return <PanelHeader onBackButton={onClickBack} showBackButton={showBackButton} onClose={onClose} centerAlignTitle title={text} />;
  };

  const saveCurrentOrderToDropbox = async (): Promise<void> => {
    if (!currentDownloadOrderId) return;
    const orderState = orderHashmap[currentDownloadOrderId];
    if (!orderState || orderState.state !== DownloadStateTypes.READY || !orderState.downloadURL) return;
    if (!isDropboxPendingSave) return;
    const downloadFilename = `${makeFilenameSafe(posterObject.name, 'poster')}.${fileType}`;
    await saveFileToDropbox(orderState.downloadURL, downloadFilename, {
      onSuccess: () => {
        showEditorNotificationMessageGrowl(window.i18next.t('pmwjs_saved'));
        setIsDropboxPendingSave(false);
      },
      onError: (err) => {
        console.error('Dropbox save error', err);
      },
    });
  };

  return (
    <Panel
      panelId={props.id}
      ref={wrapperRef}
      panelHeader={getPanelHeader()}
      className={`${className} download-panel-content-manager ${styles.panel}`}
      panelFooter={
        isDownloadInProgressOrReady || isDownloadInErrorState ? undefined : (
          <DownloadPanelFooter
            showLoading={isInitialDownloadInProgress}
            onClickDownload={onClickDownload}
            onDownloadToDrive={onDownloadToDrive}
            onDownloadToDropbox={onDownloadToDropbox}
            hasTopShadow={bottomShadow}
          />
        )
      }
      panelBodyClassName={styles.maxHeight}
      panelContent={getPanelContentWrapper()}
    />
  );
}
